/**
 * Create-or-verify flow for the caregiver PIN, shared by the board's
 * "Área do responsável" and the profile page.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessage, get, post, put } from './api';

export type PinStage = 'loading' | 'create' | 'verify' | 'done' | 'error';

export function usePinFlow(options: { active: boolean; skipIfUnlocked?: boolean; onSuccess(): void }) {
  const { active, skipIfUnlocked = false, onSuccess } = options;
  const [stage, setStage] = useState<PinStage>('loading');
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Latest callback without re-running the effects when the parent re-renders.
  const successRef = useRef(onSuccess);
  successRef.current = onSuccess;

  const load = useCallback(async () => {
    setStage('loading');
    setError(null);
    setFirstPin(null);
    try {
      const { hasPin, unlocked } = await get<{ hasPin: boolean; unlocked: boolean }>('/api/user/pin');
      if (hasPin && unlocked && skipIfUnlocked) {
        setStage('done');
        successRef.current();
      } else {
        setStage(hasPin ? 'verify' : 'create');
      }
    } catch (err) {
      setError(errorMessage(err));
      setStage('error');
    }
  }, [skipIfUnlocked]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const submit = useCallback(
    async (pin: string) => {
      setError(null);
      if (stage === 'create' && !firstPin) {
        setFirstPin(pin);
        return;
      }
      if (stage === 'create' && pin !== firstPin) {
        setFirstPin(null);
        setError('Os PINs não conferem. Comece de novo.');
        return;
      }
      setBusy(true);
      try {
        if (stage === 'create') await put('/api/user/pin', { pin });
        else await post('/api/user/pin/verify', { pin });
        setFirstPin(null);
        setStage('done');
        successRef.current();
      } catch (err) {
        setError(errorMessage(err));
      } finally {
        setBusy(false);
      }
    },
    [stage, firstPin],
  );

  return { stage, confirming: !!firstPin, busy, error, submit, retry: load };
}
